import React, { useCallback, useMemo, useState } from 'react';
import { Button, Card, Col, Form, Row } from "react-bootstrap";
import RenderDetailField from "containers/common/RenderDetailField";
import "containers/common/Detail.css";
import "containers/common/List.css";
import "containers/common/Table.css";
import { createElementIdFactory } from "utils/elementIds";

function EntityPageGeneralInfo({
    entity,
    collections,
    handleChange,
    handleBlur,
    handleImmediateSave,
    ensureSuburbOptionsLoaded,
    setUi,
    saveRecord,
    user,
    updateTableState,
}) {
    const createId = createElementIdFactory("entity-general-info");
    const [streetSuburbInput, setStreetSuburbInput] = useState("");
    const [postalSuburbInput, setPostalSuburbInput] = useState("");

    const stateOptions = useMemo(() => ([
        { value: "ACT", label: "ACT" },
        { value: "NSW", label: "NSW" },
        { value: "NT", label: "NT" },
        { value: "QLD", label: "QLD" },
        { value: "SA", label: "SA" },
        { value: "TAS", label: "TAS" },
        { value: "VIC", label: "VIC" },
        { value: "WA", label: "WA" },
        { value: "NZ", label: "NZ" },
    ]), []);

    const normalizedSuburbOptions = useMemo(() => {
        const source = collections?.suburbOptions || [];
        return source
            .map((item) => {
                if (!item) return null;

                if (typeof item === "string") {
                    return { value: item, label: item, suburb: item };
                }

                const suburb = item.suburb || item.value || item.label || "";
                const state = item.state || "";
                const postcode = item.postcode || "";
                if (!suburb) return null;

                return {
                    ...item,
                    value: item.value || suburb,
                    label: item.label || (state ? `${suburb} (${state})` : suburb),
                    suburb,
                    state,
                    postcode,
                };
            })
            .filter(Boolean);
    }, [collections?.suburbOptions]);

    const filterSuburbOptions = useCallback((input) => {
        const term = (input || "").trim().toLowerCase();
        const source = normalizedSuburbOptions;
        if (!term) return source.slice(0, 120);

        return source
            .filter((opt) => {
                const suburb = (opt.suburb || opt.value || "").toLowerCase();
                const label = (opt.label || "").toLowerCase();
                const postcode = String(opt.postcode || "").toLowerCase();
                return suburb.includes(term) || label.includes(term) || postcode.includes(term);
            })
            .slice(0, 200);
    }, [normalizedSuburbOptions]);

    const streetSuburbOptions = useMemo(
        () => filterSuburbOptions(streetSuburbInput),
        [filterSuburbOptions, streetSuburbInput]
    );

    const postalSuburbOptions = useMemo(
        () => filterSuburbOptions(postalSuburbInput),
        [filterSuburbOptions, postalSuburbInput]
    );

    const {
        entity_id,
        origin_entity_id,
        name,
        telephone,
        fax,
        notes,
        st_care_of,
        st_address_line_1,
        st_address_line_2,
        st_suburb,
        st_postcode,
        st_state,
        st_country,
        pa_care_of,
        pa_po_box,
        pa_suburb,
        pa_postcode,
        pa_state,
        pa_country,
        internet,
        email,
        inactive,
        entity_type_code,
        search_state,
    } = entity || {};

    const selectedEntityTypeOption = useMemo(() => {
        if (!entity_type_code) {
            return null;
        }

        return collections.entityTypeOptions?.find((option) => option.value === entity_type_code)
            || { value: entity_type_code, label: entity_type_code };
    }, [collections.entityTypeOptions, entity_type_code]);

    const handleSelectChange = (val, fieldName) => {
        const newValue = val?.value || "";
        let updatedEntity = { ...entity, [fieldName]: newValue };

        if (fieldName === "st_suburb" || fieldName === "pa_suburb") {
            updatedEntity[fieldName] = val?.suburb || newValue;
        }

        if (fieldName === "st_suburb") {
            updatedEntity.st_state = val?.state || updatedEntity.st_state || "";
            updatedEntity.st_postcode = val?.postcode || updatedEntity.st_postcode || "";
            updatedEntity.search_state = val?.state || updatedEntity.search_state || "";
        }

        if (fieldName === "pa_suburb") {
            updatedEntity.pa_state = val?.state || updatedEntity.pa_state || "";
            updatedEntity.pa_postcode = val?.postcode || updatedEntity.pa_postcode || "";
        }

        if (fieldName === "st_state") {
            updatedEntity.search_state = newValue;
        }

        if (newValue === "(Custom)") {
            updatedEntity[fieldName] = "";
        }

        handleChange({ target: { name: fieldName, value: updatedEntity[fieldName] } });
        handleImmediateSave(updatedEntity, fieldName);
    };

    const handleCheckboxChange = (e) => {
        const { name, checked } = e.target;
        const updatedEntity = { ...entity, [name]: checked };
        handleChange(e);
        handleImmediateSave(updatedEntity, name, { forceSave: true });
    };

    const handleAddressClear = (type) => {
        const streetFields = ['st_care_of', 'st_address_line_1', 'st_address_line_2', 'st_suburb', 'st_state', 'st_postcode', 'st_country'];
        const postalFields = ['pa_care_of', 'pa_po_box', 'pa_suburb', 'pa_state', 'pa_postcode', 'pa_country'];
        const fieldsToClear = type === 'street' ? streetFields : postalFields;
        const updatedEntity = { ...entity };
        fieldsToClear.forEach(field => {
            updatedEntity[field] = "";
        });
        handleImmediateSave(updatedEntity);
    };

    const validateAndHandleBlur = (fieldName, value) => {
        const trimmedValue = (value || "").trim();

        if (fieldName === "email" && trimmedValue) {
            const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedValue);
            if (!isValidEmail) {
                const message = "The email is not a correct format";
                setUi(prev => ({ ...prev, errorMessage: message, showAlert: true }));
                return;
            }
        }

        if (fieldName === "internet" && trimmedValue) {
            const isValidWebsite = /^www\.[^.\s]+\.[^\s]+$/i.test(trimmedValue);
            if (!isValidWebsite) {
                const message = "The url (website) is not a correct format. Please enter www.{}.{}";
                setUi(prev => ({ ...prev, errorMessage: message, showAlert: true }));
                return;
            }
        }

        handleBlur({
            fieldName,
            updatedEntity: { ...entity, [fieldName]: value, modified: true }
        });
    };

    return (
        <Form.Group as={Row}  >
            <Col sm={12} md={6} lg={3}>
                <Card>
                    <Card.Header>Heading </Card.Header>
                    <Card.Body>
                        {/* Primary Identification */}
                        <RenderDetailField
                            id={createId("entity-id")}
                            label="ID"
                            value={entity_id && entity_id !== "new" ? entity_id : ''}
                            readOnly={true}
                        />
                        <RenderDetailField
                            id={createId("origin-entity-id")}
                            name="origin_entity_id"
                            label="* Entity Code"
                            value={origin_entity_id}
                            onChange={handleChange}
                            onBlur={(e) => {
                                handleBlur({
                                    fieldName: "origin_entity_id",
                                    updatedEntity: { ...entity, origin_entity_id: e.target.value, modified: true }
                                });
                            }}
                        />
                        <RenderDetailField
                            id={createId("inactive")}
                            label="Inactive"
                            value={
                                <Form.Check.Input
                                    type="checkbox"
                                    name="inactive"
                                    checked={!!inactive}
                                    onChange={handleCheckboxChange}
                                    className="m-0"
                                    style={{ width: '1.2em', height: '1.2em', cursor: 'pointer' }}
                                />
                            }
                        />
                        <RenderDetailField
                            id={createId("name")}
                            name="name"
                            label="* Name"
                            value={name}
                            onChange={handleChange}
                            onBlur={handleBlur}
                        />
                        <RenderDetailField
                            id={createId("telephone")}
                            name="telephone"
                            label="Telephone"
                            value={telephone}
                            onChange={handleChange}
                            onBlur={handleBlur}
                        />
                        <RenderDetailField
                            id={createId("fax")}
                            name="fax"
                            label="Fax"
                            value={fax}
                            onChange={handleChange}
                            onBlur={handleBlur}
                        />
                        <RenderDetailField
                            id={createId("email")}
                            name="email"
                            label="Email"
                            value={email}
                            onChange={handleChange}
                            onBlur={(e) => validateAndHandleBlur("email", e.target.value)}
                            inputGroupText="@"
                            inputGroupTextClassName="entity-general-prefix"
                            inputGroupTextStyle={{ fontSize: '12px' }}
                        />
                        <RenderDetailField
                            id={createId("internet")}
                            name="internet"
                            label="Internet"
                            value={internet}
                            onChange={handleChange}
                            onBlur={(e) => validateAndHandleBlur("internet", e.target.value)}
                            inputGroupText="www."
                            inputGroupTextClassName="entity-general-prefix"
                            inputGroupTextStyle={{ fontSize: '12px' }}
                        />
                    </Card.Body>
                </Card>
            </Col>

            <Col sm={12} md={6} lg={3}>
                <Card>
                    <Card.Header>Street Address</Card.Header>
                    <Card.Body>
                        {/* Street Address Details */}
                        <RenderDetailField id={createId("st-care-of")} name="st_care_of" label="Care Of" value={st_care_of || ''} onChange={handleChange}
                            onBlur={(e) => {
                                handleBlur({
                                    fieldName: "st_care_of",
                                    updatedEntity: { ...entity, st_care_of: e.target.value, modified: true }
                                });
                            }} />
                        <RenderDetailField id={createId("st-address-line-1")} name="st_address_line_1" label="Address Line 1" value={st_address_line_1 || ''} onChange={handleChange}
                            onBlur={(e) => {
                                handleBlur({
                                    fieldName: "st_address_line_1",
                                    updatedEntity: { ...entity, st_address_line_1: e.target.value, modified: true }
                                });
                            }} />
                        <RenderDetailField id={createId("st-address-line-2")} name="st_address_line_2" label="Address Line 2" value={st_address_line_2 || ''} onChange={handleChange}
                            onBlur={(e) => {
                                handleBlur({
                                    fieldName: "st_address_line_2",
                                    updatedEntity: { ...entity, st_address_line_2: e.target.value, modified: true }
                                });
                            }} />
                        <RenderDetailField
                            id={createId("st-suburb")}
                            label="Suburb"
                            isSelect={true}
                            options={streetSuburbOptions}
                            value={st_suburb ? { label: st_state ? `${st_suburb} (${st_state})` : st_suburb, value: st_suburb, suburb: st_suburb, state: st_state, postcode: st_postcode } : null}
                            onChange={(val) => handleSelectChange(val, "st_suburb")}
                            onFocus={ensureSuburbOptionsLoaded}
                            onInputChange={(val) => {
                                setStreetSuburbInput(val);
                                return val;
                            }}
                        />
                        <RenderDetailField
                            id={createId("st-state")}
                            name="st_state"
                            label="* State"
                            isSelect={true}
                            options={stateOptions}
                            value={st_state ? { value: st_state, label: st_state } : null}
                            onChange={(val) => handleSelectChange(val, "st_state")}
                            isClearable={true}
                        />
                        <RenderDetailField id={createId("st-postcode")} name="st_postcode" label="Postcode" value={st_postcode} onChange={handleChange}
                            onBlur={(e) => {
                                handleBlur({
                                    fieldName: "st_postcode",
                                    updatedEntity: { ...entity, st_postcode: e.target.value, modified: true }
                                });
                            }} />
                        <RenderDetailField
                            id={createId("st-country")}
                            label="Country"
                            isSelect={true}
                            options={collections.countryOptions}
                            value={st_country ? { label: st_country, value: st_country } : null}
                            onChange={(val) => handleSelectChange(val, "st_country")}
                        />

                        <Form.Group as={Row} className="mt-2 text-center">
                            <Col sm={6} md={6} lg={4}></Col>
                            <Col className="d-flex justify-content-center gap-2">
                                <Button variant="secondary" onClick={() => handleAddressClear('street')} size="sm" id={createId("clear-street", "button")}>Clear </Button>
                            </Col>
                        </Form.Group>
                    </Card.Body>
                </Card>
            </Col>

            <Col sm={12} md={6} lg={3}>
                <Card>
                    <Card.Header>Postal Address</Card.Header>
                    <Card.Body>
                        {/* Postal Address Section */}
                        <RenderDetailField id={createId("pa-care-of")} name="pa_care_of" label="Care Of" value={pa_care_of} onChange={handleChange}
                            onBlur={(e) => {
                                handleBlur({
                                    fieldName: "pa_care_of",
                                    updatedEntity: { ...entity, pa_care_of: e.target.value, modified: true }
                                });
                            }} />
                        <RenderDetailField id={createId("pa-po-box")} name="pa_po_box" label="PO Box" value={pa_po_box} onChange={handleChange}
                            onBlur={(e) => {
                                handleBlur({
                                    fieldName: "pa_po_box",
                                    updatedEntity: { ...entity, pa_po_box: e.target.value, modified: true }
                                });
                            }} />
                        <RenderDetailField
                            id={createId("pa-suburb")}
                            label="Suburb"
                            isSelect={true}
                            options={postalSuburbOptions}
                            value={pa_suburb ? { label: pa_state ? `${pa_suburb} (${pa_state})` : pa_suburb, value: pa_suburb, suburb: pa_suburb, state: pa_state, postcode: pa_postcode } : null}
                            onChange={(val) => handleSelectChange(val, "pa_suburb")}
                            onFocus={ensureSuburbOptionsLoaded}
                            onInputChange={(val) => {
                                setPostalSuburbInput(val);
                                return val;
                            }}
                        />
                        <RenderDetailField
                            id={createId("pa-state")}
                            name="pa_state"
                            label="State"
                            isSelect={true}
                            options={stateOptions}
                            value={pa_state ? { value: pa_state, label: pa_state } : null}
                            onChange={(val) => handleSelectChange(val, "pa_state")}
                            isClearable={true}
                        />
                        <RenderDetailField id={createId("pa-postcode")} name="pa_postcode" label="Postcode" value={pa_postcode} onChange={handleChange}
                            onBlur={(e) => {
                                handleBlur({
                                    fieldName: "pa_postcode",
                                    updatedEntity: { ...entity, pa_postcode: e.target.value, modified: true }
                                });
                            }} />
                        <RenderDetailField
                            id={createId("pa-country")}
                            label="Country"
                            isSelect={true}
                            options={collections.countryOptions}
                            value={pa_country ? { label: pa_country, value: pa_country } : null}
                            onChange={(val) => handleSelectChange(val, "pa_country")}
                        />

                        <Form.Group as={Row} className="mt-3">
                            <Col sm={6} md={6} lg={4}></Col>
                            <Col>
                                <Button variant="secondary" onClick={() => handleAddressClear('postal')} size="sm" id={createId("clear-postal", "button")}> Clear</Button>
                            </Col>
                        </Form.Group>
                    </Card.Body>
                </Card>
            </Col>

            <Col sm={12} md={6} lg={3}>
                <Card id={createId("notes", "card")}  >
                    <Card.Header>Notes</Card.Header>
                    <Card.Body>
                        <RenderDetailField
                            id={createId("entity-type-code")}
                            name="entity_type_code"
                            label="Entity Type"
                            isSelect={true}
                            options={collections.entityTypeOptions}
                            value={selectedEntityTypeOption}
                            onChange={(val) => handleSelectChange(val, "entity_type_code")}
                        />
                        <RenderDetailField
                            id={createId("notes")}
                            name="notes"
                            label="Notes"
                            value={notes}
                            onChange={handleChange}
                            onBlur={(e) => {
                                handleBlur({
                                    fieldName: "notes",
                                    updatedEntity: { ...entity, notes: e.target.value, modified: true }
                                });
                            }}
                            as="textarea"
                            rows={16}
                            controlClassName="entity-notes-textarea"
                            controlStyle={{ minHeight: "190px" }}
                        />
                        <RenderDetailField
                            id={createId("search-state")}
                            label="Search State"
                            value={search_state || ''}
                            readOnly={true}
                        />
                    </Card.Body>
                </Card>
            </Col>
        </Form.Group>
    );
}

export default EntityPageGeneralInfo;